import React, { useState, useRef, useMemo, useCallback } from 'react';
import { prefixObjectValues, enums } from '../../../utils';
import * as dm from '../../../data-models';
import dayjs from 'dayjs';
import { dateFormat } from '../../base/claim-card/card-items/shared-styles';
import { Text } from '../../base/tw-components';
import { Props } from '../../start-a-claim';
import { useHistory, Link } from 'react-router-dom';
import Button from '../../base/button';
import { ButtonTypes } from '../../../utils/enums';
import ConfirmDialogModal from '../../base/confirm-dialog-modal';

interface IClaimCard {
  keyId?: number;
  claim: dm.GeneratedTypes;
}

type DateInput = string | number | Date;
interface Leave {
  startDate?: DateInput;
  endDate?: DateInput;
}

export const Card = React.forwardRef<HTMLDivElement, Props>(
  ({ children, id, className }, ref) => {
    return (
      <div
        {...(id ? { id } : {})}
        ref={ref}
        className={`tw-bg-white tw-rounded-[8px] tw-shadow-[0_3px_6px_rgba(46,46,46,0.16)] tw-max-w-[992px] tw-overflow-hidden tw-w-full${
          className ? ` ${className}` : ''
        }`}
      >
        {children}
      </div>
    );
  },
);

export const CardHeader = ({ children, id, className }: Props) => {
  const baseClasses =
    'tw-items-stretch tw-bg-white tw-border-l-[16px] tw-border-unm-interactive01-400 ' +
    'tw-rounded-[8px] tw-shadow-[0_3px_6px_rgba(46,46,46,0.16)] tw-flex tw-relative ' +
    'tw-w-full tw-z-[2]';

  const classes = [baseClasses, className].filter(Boolean).join(' ');

  return (
    <div {...(id ? { id } : {})} className={classes}>
      {children}
    </div>
  );
};

export const BreadcrumbText = ({ children, id }: Props) => {
  return (
    <p
      {...(id ? { id } : {})}
      className="tw-text-unm-primaryblue tw-cursor-pointer tw-m-0 tw-underline"
    >
      {children}
    </p>
  );
};

const domIdsStatic = {
  rootNode: 'claims-card',
  cardTitle: 'claim-card-title',
};

export const domIdsUnique = (prefix?: string) =>
  prefixObjectValues(prefix, domIdsStatic);

const getCardTitle = (claim: any) => {
  if (claim?.leaves?.length > 0) {
    return claim.leaves[0].reason ===
      enums.PregnancyTextChanges.pregnancyBondingLiteral
      ? enums.PregnancyTextChanges.pregnancyBondingLiteralNew
      : claim.leaves[0].reason;
  } else if (claim?.claims?.length > 0) {
    return claim.claims[0].claimType;
  } else if (claim?.accommodations?.length > 0) {
    return 'Accommodation Request';
  } else if (claim?.reason) {
    return claim?.reason;
  }
  return 'N/A';
};

const ClaimDateRange = ({
  claim,
  isClaimInProcess,
}: {
  claim?: {
    NTNId?: string;
    startDate?: DateInput;
    endDate?: DateInput;
    leaves?: Leave[];
  } | null;
  isClaimInProcess: boolean;
}) => {
  if (!claim) return null;

  const startDate = isClaimInProcess
    ? claim?.startDate
    : claim?.leaves?.[0]?.startDate;

  const endDate = isClaimInProcess
    ? claim?.endDate
    : claim?.leaves?.[0]?.endDate;

  if (!startDate || !endDate) return null;

  return (
    <div className="tw-flex tw-items-center md:tw-col-span-4 xl:tw-col-span-5">
      <Text
        color="unm-primary02-500"
        fontSize="base"
        className="tw-hidden sm:tw-block md:tw-hidden xl:tw-block"
      >
        Requested:&nbsp;
      </Text>
      <div className="tw-flex tw-items-center">
        <Text color="unm-primary02-500" fontSize="base">
          {dayjs(startDate).format(dateFormat)} –{' '}
          {dayjs(endDate).format(dateFormat)}
        </Text>
      </div>
    </div>
  );
};

const BasicClaimsCard: (p: IClaimCard) => React.ReactElement = (p) => {
  const { claim, keyId } = p;
  const domIds = domIdsUnique(`sc-${keyId}-`);
  const NTNId = claim?.NTNId;

  const dataToPass = useMemo(
    () => ({ ntnToOpen: NTNId, tabToOpen: 'Details' }),
    [NTNId],
  );

  const [showInfo, setShowInfo] = useState(false);
  const history = useHistory();

  const isClaimInProcess = useMemo(
    () => (claim?.NTNId || '').toLowerCase() === 'claim in process',
    [claim?.NTNId],
  );

  const revealRefs = useRef<Record<string | number, HTMLDivElement | null>>({});

  // Defer navigation one frame to let modal close and avoid racing animations.
  const rafIdRef = useRef<number | null>(null);

  const navigateToExtension = useCallback(() => {
    rafIdRef.current = requestAnimationFrame(() => {
      history.push({
        pathname: enums.AppPaths.extensionRequest,
        state: dataToPass,
      });
    });
  }, [history, dataToPass]);

  const onConfirmModal = useCallback(() => {
    setShowInfo(false);
    navigateToExtension();
  }, [setShowInfo, navigateToExtension]);

  const onCancelModal = useCallback(() => {
    setShowInfo(false);
  }, [setShowInfo]);

  return (
    <>
      <Card
        id={domIds.rootNode}
        key={NTNId}
        ref={(el) => (revealRefs.current[NTNId] = el)}
      >
        {/* ------------------------------------ Mobile View ------------------------------------ */}
        <CardHeader className="tw-flex-col md:tw-hidden tw-px-4 tw-py-2">
          <div className="tw-flex tw-w-full tw-justify-between tw-items-center tw-pb-2">
            <div className="tw-text-unm-interactive01-800 tw-font-Sans tw-font-normal tw-text-2xl">
              {getCardTitle(claim)}
            </div>
          </div>

          <div className="tw-flex tw-flex-row tw-w-full tw-justify-between tw-items-center tw-pb-3">
            <Text
              fontSize="base"
              fontWeight="semibold"
              color="unm-primary02-500"
              className="tw-pr-6 tw-break-words"
            >
              {NTNId}
            </Text>
            <ClaimDateRange claim={claim} isClaimInProcess={isClaimInProcess} />
          </div>
          {!isClaimInProcess && (
            <div className="tw-flex tw-flex-row tw-w-full tw-justify-between tw-items-center tw-pb-1">
              <Link
                to={{
                  pathname: enums.AppPaths.appRoot,
                  state: dataToPass,
                }}
                id={domIds.goBack}
                data-gtm-dl={JSON.stringify({
                  attribute1: 'file a claim',
                  attribute2: 'simmilar claim found - view details',
                  attribute3: 'claim_interaction',
                  attribute4: 'undefined',
                })}
                className="tw-flex tw-flex-row tw-items-center tw-pr-6"
              >
                <BreadcrumbText>view details</BreadcrumbText>
              </Link>
              <Button
                className=" tw-px-3 tw-py-0.5 tw-justify-self-end"
                buttonStyle={ButtonTypes.primary}
                hPadding={12}
                id={domIds.newClaimBtn}
                onClick={() => setShowInfo(true)}
                data-gtm-dl={JSON.stringify({
                  attribute1: 'file a claim',
                  attribute2: 'simmilar claim found - request extension',
                  attribute3: 'claim_interaction',
                  attribute4: 'undefined',
                })}
              >
                Request Extension
              </Button>
            </div>
          )}
        </CardHeader>

        {/* ------------------------------------ Desktop View ------------------------------------ */}

        <CardHeader className="tw-hidden md:tw-block tw-flex-col tw-px-6 tw-py-3">
          <div className="tw-flex tw-w-full md:tw-pb-2 tw-items-center">
            <div
              id={domIds.cardTitle}
              className="tw-flex-row tw-w-full tw-text-unm-interactive01-800 tw-font-Sans tw-font-normal tw-text-[28px] tw-leading-[1.2em] tw-break-words"
            >
              {getCardTitle(claim)}
            </div>
          </div>

          <div className="md:tw-grid md:tw-grid-cols-12 md:tw-gap-x-2 md:tw-gap-y-2 md:tw-items-center md:tw-pb-1">
            <Text
              className="tw-pr-6 md:tw-pr-0 tw-break-words md:tw-col-span-3 lg:tw-col-span-2"
              fontSize="base"
              fontWeight="semibold"
              color="unm-primary02-500"
            >
              {NTNId}
            </Text>

            <div className="tw-flex tw-items-center md:tw-col-span-3 lg:tw-col-span-4 xl:tw-col-span-5">
              <ClaimDateRange
                claim={claim}
                isClaimInProcess={isClaimInProcess}
              />
            </div>
            {!isClaimInProcess && (
              <>
                <Link
                  to={{
                    pathname: enums.AppPaths.appRoot,
                    state: dataToPass,
                  }}
                  id={domIds.goBack}
                  data-gtm-dl={JSON.stringify({
                    attribute1: 'file a claim',
                    attribute2: 'simmilar claim found - view details',
                    attribute3: 'claim_interaction',
                    attribute4: 'undefined',
                  })}
                  className="md:tw-col-span-2 tw-justify-self-end"
                >
                  <BreadcrumbText>view details</BreadcrumbText>
                </Link>
                <Button
                  className="tw-w-full md:tw-w-auto tw-px-3 tw-py-1.5
               md:tw-col-span-4 xl:tw-col-span-3 tw-justify-self-end"
                  buttonStyle={ButtonTypes.primary}
                  id={domIds.newClaimBtn}
                  onClick={() => setShowInfo(true)}
                  data-gtm-dl={JSON.stringify({
                    attribute1: 'file a claim',
                    attribute2: 'simmilar claim found - request extension',
                    attribute3: 'claim_interaction',
                    attribute4: 'undefined',
                  })}
                >
                  Request Extension
                </Button>
              </>
            )}
          </div>
        </CardHeader>
      </Card>
      {showInfo && (
        <ConfirmDialogModal
          title={'Request Extension'}
          description={`Are you sure you want to request an extension for ${NTNId} ?`}
          markdown={true}
          confirmButtonText={'Continue'}
          cancelButtonText={'Cancel'}
          modalCallback={onCancelModal}
          handleConfirm={onConfirmModal}
          data-attrib1={'file a claim'}
          dataAttributes={{
            'data-attribute1': 'file a claim',
            'data-confirm': `claim_interaction`,
            'data-cancel': `claim_interaction`,
            'data-link-url': enums.AppPaths.extensionRequest,
          }}
        />
      )}
    </>
  );
};

export default BasicClaimsCard;
